import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { useSearch } from '../../contexts/SearchContext';
import { documentService, type ViewMode } from '../../services/documentService';
import { UploadModal } from '../../components/documents';
import {
    ConfirmationModal,
    DocumentFeed,
    DocumentsPageLayout,
    DocumentPreview,
    PageLoadOverlay,
    type SortOption,
    type BreadcrumbItem,
    type PreviewFact,
} from '@hofsync/design-system';
import { useDocuments } from '../../hooks/useDocuments';
import { useUnlinkedCount } from '../../hooks/useUnlinkedCount';
import { usePageLoadTelemetry } from '../../hooks/usePageLoadTelemetry';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useTenantScopeId } from '../../hooks/useTenantScopeId';
import { ALL_DOCUMENTS_LENS_ID, buildCategories } from './categoryConfig';
import { readStoredCategory, resolveStoredCategory, writeStoredCategory } from './categoryPreference';
import { buildPreviewFacts } from './previewFacts';
import { documentRows } from '../../components/twinLinks/lineRows';
import { useSelectionMode } from './useSelectionMode';
import { usePreviewState } from './usePreviewState';
import { useDocumentMetadata, useTaxonomyDomains, useOverrideClassification, useUpdateFactOverrides, useCorrectFact } from './useDocumentMetadata';
import { useTagActions } from './useTagActions';
import { useDocumentActions } from './useDocumentActions';
import DocumentHistorySection from './components/DocumentHistorySection';
import DocumentVerifiedBadgeContainer from '../../components/documents/DocumentVerifiedBadgeContainer';
import DocumentTwinLinksContainer from './DocumentTwinLinksContainer';
import { fetchEntitySuggestions } from './entitySuggestions';
import './DocumentsPage.css';

/**
 * Deep-link ids can carry an attachment-index suffix (``<uuid>_att_1``)
 * that points at one attachment of a multi-document email upload.
 *
 * #1849 — feed-row matching and ``expandedDocId`` NO LONGER strip this:
 * each attachment is a first-class document with its own ``_att_<n>`` id +
 * feed row + metadata/preview endpoints, so the exact id is what selects the
 * right row (stripping to the bare parent collapsed siblings onto one id and
 * pointed at a row that often doesn't exist). This helper is retained only
 * for the #1193 edit-mode parent lookup below, and remains exported +
 * unit-tested because the suffix grammar is load-bearing there.
 * Ids without the suffix pass through unchanged.
 */
export function stripAttachmentSuffix(docId: string | null): string | null {
    if (!docId) return docId;
    return docId.replace(/_att_\d+$/, '');
}

const DocumentsPage: React.FC = () => {
    const { t } = useTranslation();
    const { showToast, showProgressToast } = useToast();
    const { searchQuery } = useSearch();
    const auditReadEnabled = useFeatureFlag('audit_events.read_path');
    // taxonomy-v2 is the sole render path. The whole documents UI (lens nav,
    // counts source, document-row + metadata labels) renders the native
    // (domain, role) cell. The legacy v1 dual-path was collapsed with the
    // classifier-v1 terminal decommission (#2009); back-catalogue docs carry a
    // translated cell from the v1→v2 backfill so they appear under the correct
    // v2 lens.
    const [searchParams, setSearchParams] = useSearchParams();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<string>(
        searchParams.get('category') ?? ALL_DOCUMENTS_LENS_ID,
    );
    const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(
        searchParams.get('sub'),
    );
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    // #1849 — ``expandedDocId`` holds the EXACT document id from ``?doc=``,
    // including any ``_att_<n>`` attachment suffix. Each email attachment is a
    // first-class document with its own ``<parent>_att_<n>`` id and its own feed
    // row (the worker splits attachments into separate documents; the list path
    // does no parent-collapse), and every ``/metadata`` · ``/preview`` ·
    // page-resolve endpoint is keyed on that same first-class id. Stripping to
    // the bare parent UUID (the old #1175 behaviour) pointed at a row that often
    // doesn't exist and collapsed sibling attachments (``_att_0``/``_att_1``)
    // onto one id — so the clicked row never matched on the first click.
    const [expandedDocId, setExpandedDocId] = useState<string | null>(
        searchParams.get('doc'),
    );

    // HOF-106 — which tenant's remembered lens applies. Withheld (``resolved``
    // false) while auth is in flight; the restore effect below must not spend
    // its one shot on an unresolved tenant. Mechanics are HOF-101's, reused as
    // built (see ``categoryPreference``).
    const { tenantId, resolved: tenantResolved } = useTenantScopeId();
    // Read once per tenant, not in a lazy ``useState`` initializer: the tenant
    // arrives with ``/auth/me``, so its namespace is not known at mount, and
    // re-reading when the scope changes is what makes „Anzeige als" hand over
    // the right farm's lens.
    const storedCategoryRaw = useMemo(
        () => (tenantResolved ? readStoredCategory(tenantId) : null),
        [tenantResolved, tenantId],
    );
    // The restore is a ONE-SHOT per mount. Clearing the lens later — the focus
    // panel's ``navigate('/documents')``, the back button, the all-documents
    // breadcrumb — must be respected; an effect that re-stamps whenever
    // ``?category=`` goes missing would make the all-lens unclickable.
    const preferenceRestored = useRef(false);

    // Sync URL-tracking state when ``searchParams`` changes externally —
    // i.e. focus-panel "Dokumente" click clears the URL via
    // ``navigate('/documents', { replace: true })``, browser back/forward,
    // or any other external navigation that doesn't go through this
    // component's handlers. Without this, the ``useState`` initializers
    // above only run at mount; the URL becomes the source of truth at
    // mount and the state then drifts independently — a prior category or
    // ``expandedDocId`` would survive a bare-``/documents`` navigation and
    // the page would no longer reflect the URL's all-documents state.
    // React skips re-renders when the new state equals the current
    // value, so the existing handlers that update both state + URL in
    // lockstep don't loop.
    useEffect(() => {
        setSelectedCategory(searchParams.get('category') ?? ALL_DOCUMENTS_LENS_ID);
        setSelectedSubcategory(searchParams.get('sub'));
        // #1849 — keep the full id (incl. any ``_att_<n>``) so the feed row
        // matches exactly; see the ``expandedDocId`` initializer above.
        setExpandedDocId(searchParams.get('doc'));
    }, [searchParams]);

    // Use the unified document state hook with pagination and server-side filtering
    const {
        feedData,
        loading,
        error,
        uploadBatch,
        refreshDocuments,
        updateDocumentTagsOptimistic,
        currentPage,
        total,
        pageSize,
        setPage,
        sortBy,
        setSortBy,
        viewMode,
        setViewMode,
        newDocsCount,
        markAsViewed,
        domainCounts,
        totalAllVisible,
    } = useDocuments({
        // classifier-v3 (#1066): the selected lens IDs are the native taxonomy-v2
        // (domain, role) axes; the synthetic all-lens clears the domain filter.
        domain: selectedCategory !== ALL_DOCUMENTS_LENS_ID ? selectedCategory : null,
        role: selectedSubcategory,
        search: searchQuery.trim() || null,
        showDeleted,
    });

    // Issue #643 — page-load telemetry overlay. ``loading`` from
    // useDocuments is the primary-load signal (cold mount, category /
    // sub-category switch, filter change, page change). The view
    // descriptor includes filters so slow loads can be correlated to
    // specific filter combinations in Kibana. Per-document sub-loads
    // (preview metadata, taxonomy tree) intentionally don't contribute.
    const documentsView = useMemo(() => {
        const parts = [`documents:category=${selectedCategory}`];
        if (selectedSubcategory) parts.push(`sub=${selectedSubcategory}`);
        if (searchQuery.trim()) parts.push('search=present');
        if (showDeleted) parts.push('deleted=true');
        return parts.join(':');
    }, [selectedCategory, selectedSubcategory, searchQuery, showDeleted]);

    const pageLoadOverlay = usePageLoadTelemetry({
        page: 'documents',
        view: documentsView,
        isLoading: loading,
        pageIndex: currentPage,
        pageSize,
    });

    // Selection mode (with cross-page select-all support)
    const filterParams = useMemo(() => ({
        // classifier-v3 (#1066): the native (domain, role) lens axes scope
        // cross-page select-all to the active lens.
        domain: selectedCategory !== ALL_DOCUMENTS_LENS_ID ? selectedCategory : null,
        role: selectedSubcategory || null,
        search: searchQuery.trim() || null,
        view: viewMode,
        tag: null as string | null,
        deletedOnly: showDeleted,
    }), [selectedCategory, selectedSubcategory, searchQuery, viewMode, showDeleted]);

    const {
        selectionMode,
        selectedDocIds,
        handleEnterSelectionMode,
        handleExitSelectionMode,
        handleSelectionChange,
        handleSelectAll,
        handleDeselectAll,
        allMatchingSelected,
        showSelectAllBanner,
        handleSelectAllMatching,
        loadingAllIds,
        setSelectionMode,
        setSelectedDocIds,
    } = useSelectionMode({ feedData, total, filterParams });

    // Mark documents as deleted when viewing deleted documents (must be before usePreviewState for documentList)
    const documentsWithDeletedFlag = useMemo(() => {
        if (!showDeleted) return feedData;
        return feedData.map((doc) => ({ ...doc, isDeleted: true }));
    }, [feedData, showDeleted]);

    // Ordered list of doc IDs for prev/next navigation in preview
    const previewDocumentList = useMemo(
        () => documentsWithDeletedFlag.map((d) => d.doc_id),
        [documentsWithDeletedFlag],
    );

    // Document preview overlay
    const {
        previewState,
        rotating,
        handlePreview,
        handlePreviewClose,
        handlePreviewDownload,
        handlePreviewRotate,
        handleNavigate,
        canNavigatePrev,
        canNavigateNext,
    } = usePreviewState({ t, documentList: previewDocumentList });

    // Document metadata (extracted facts + classification) for the preview panel
    const { data: metadata, isLoading: metadataLoading } = useDocumentMetadata(
        previewState.isOpen ? previewState.currentDocId : null,
    );
    // #755 — also fetch metadata for the currently expanded row so the
    // page-preview tiles can read ``page_count`` before the modal opens.
    // React Query dedups against the modal-side fetch, so an expand →
    // open sequence costs at most one ``/metadata`` round-trip.
    const { data: expandedMetadata } = useDocumentMetadata(expandedDocId);
    const expandedPageCount = expandedMetadata?.page_count ?? undefined;
    const getPageCount = useCallback(
        (docId: string) => (docId === expandedDocId ? expandedPageCount ?? null : null),
        [expandedDocId, expandedPageCount],
    );
    const pageThumbnailUrl = useCallback(
        (docId: string, page: number) => documentService.getPageThumbnailUrl(docId, page),
        [],
    );
    const onPreviewPageTile = useCallback(
        (docId: string, page: number) => {
            const doc = documentsWithDeletedFlag.find((d) => d.doc_id === docId);
            void handlePreview(docId, doc?.smartFilename || doc?.filename, page);
        },
        [documentsWithDeletedFlag, handlePreview],
    );

    // #1175 — deep-link "edit mode". A ``/documents?doc=<id>&mode=edit``
    // link (emitted by the GS-check Edit pencil) should open the doc
    // straight into the preview/edit overlay — the surface whose fact
    // edits route through the correction/override endpoints (see
    // ``handleFactEdit`` below) — rather than only expanding the feed row.
    // We open the overlay once per deep-linked id, then strip ``mode``
    // from the URL so a manual close / refresh doesn't immediately
    // reopen it. The feed row also stays expanded (``expandedDocId`` is
    // still seeded), so closing the overlay leaves the user on the
    // unfolded row.
    const editModeHandledRef = useRef<string | null>(null);
    useEffect(() => {
        if (searchParams.get('mode') !== 'edit') {
            // #1767 — the one-shot has been consumed: ``mode`` has left the URL
            // (either the strip below committed, or we arrived without it). Free
            // the guard so a fresh ``mode=edit`` navigation for the SAME doc
            // reopens the overlay after it was closed. This runs only once
            // ``mode`` is absent — i.e. in a render where the open below would
            // not fire anyway — so it cannot reintroduce the double-open the ref
            // guards against (the ref stays pinned for the whole window
            // ``mode=edit`` is present). A re-click while the overlay is still
            // open re-fetches the same doc (overlay stays open on it, no close);
            // that redundant fetch is the cost of keeping this reset ordering-safe.
            editModeHandledRef.current = null;
            return;
        }
        // #1193 — preview the EXACT selected document, keeping any ``_att_<n>``
        // attachment suffix. The GS-check Edit pencil targets a specific
        // attachment (e.g. the Gutschrift inside a multi-doc email upload), so
        // previewing anything but the exact id would open the wrong document.
        // Since #1849 the feed row also expands against this same exact id
        // (``expandedDocId``), so closing the overlay leaves the user on the
        // correct unfolded attachment row.
        const targetId = searchParams.get('doc');
        if (!targetId) return;
        // Guard against re-firing on every render / searchParams identity
        // change while the same id is in flight.
        if (editModeHandledRef.current === targetId) return;
        editModeHandledRef.current = targetId;

        // Best-effort filename label for the loading overlay; the preview
        // fetches the authoritative name anyway. Retained here as the last
        // internal caller of stripAttachmentSuffix (parent-row lookup fallback).
        const parentId = stripAttachmentSuffix(targetId);
        const doc = documentsWithDeletedFlag.find((d) => d.doc_id === parentId);
        void handlePreview(targetId, doc?.smartFilename || doc?.filename);

        // Drop the one-shot ``mode`` param; keep ``doc`` so the row stays
        // expanded behind the overlay.
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('mode');
            return next;
        }, { replace: true });
    }, [searchParams, documentsWithDeletedFlag, handlePreview, setSearchParams]);

    // #1175 — windowing fallback. The feed is server-paginated (2700+
    // docs), so a deep-linked row may not be on the current page/filter
    // at all — nothing to expand or scroll to. When that happens, use the
    // document's own classification (fetched via ``expandedMetadata``) to
    // auto-navigate to its category so the row lands in a much smaller,
    // navigable set. Fires once per id and only when the row is genuinely
    // absent after the feed finished loading; the in-feed common case
    // (recent docs, GS-check targets) skips this entirely.
    const categoryJumpHandledRef = useRef<string | null>(null);
    useEffect(() => {
        const targetId = expandedDocId;
        if (!targetId || loading) return;
        // Row already on the current page → DocumentFeed handles scroll +
        // unfold; no navigation needed.
        if (feedData.some((d) => d.doc_id === targetId)) {
            categoryJumpHandledRef.current = targetId;
            return;
        }
        if (categoryJumpHandledRef.current === targetId) return;
        // taxonomy-v2 default (#1373): the lens IDs are the v2 domain, so the
        // category-jump targets classification.domain; fall back to the legacy
        // category for any doc whose translated cell is somehow absent.
        const targetCategory =
            expandedMetadata?.classification?.domain ?? expandedMetadata?.classification?.category;
        if (!targetCategory) return; // metadata not loaded yet — wait
        categoryJumpHandledRef.current = targetId;
        // Already filtered to the right category but still off-page (page
        // 2+). We can't compute the exact page without an order-matched
        // locate endpoint, so leave the user on the category's first page
        // rather than loop. Only navigate when the category actually differs.
        if (targetCategory === selectedCategory) {
            // #2009 slice 2: the domain already matches, so the sub-drop the
            // domain-jump does below (``handleCategorySelect(_, null)``) never
            // runs — but a stale/mismatched ``?sub=`` still hides the target. A
            // pre-#2009 deep-link carries a LEGACY subcategory in ``sub=`` whose
            // vocab differs from the doc's v2 role, and ``selectedSubcategory``
            // is forwarded as an EXACT ``role=`` filter to BOTH the feed query
            // and the page-resolve — so a real v3 doc whose true role differs is
            // filtered out (found=false). Clear the stale sub in place so the
            // deep-link can surface the doc.
            const targetRole = expandedMetadata?.classification?.role ?? null;
            if (selectedSubcategory && selectedSubcategory !== targetRole) {
                setSelectedSubcategory(null);
                setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('sub');
                    return next;
                }, { replace: true });
            }
            return;
        }
        handleCategorySelect(targetCategory, null);
    }, [expandedDocId, loading, feedData, expandedMetadata, selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

    // #1187 — beyond-first-window page resolution. The category-jump above
    // lands the user in the deep-linked doc's category, but a row on page 2+
    // of that category still never mounts (the feed is server-paginated, one
    // page at a time). Once we're filtered to the right category and the row
    // is still absent, ask the backend for its offset within the SAME filtered
    // + ordered result set and jump straight to that page — the #1175 scroll +
    // unfold then runs as usual. Bounded: one lookup per id, and we give up
    // gracefully (stay on the category's first page) when the doc can't be
    // resolved (filtered out / unknown / lookup error).
    // Once-guard for page resolution, keyed by the full deep-link + filter
    // "intent" (not just the doc id): if the user changes sort / search /
    // subcategory / deleted-view for the same ``?doc=``, the row may no longer be
    // on page 0 under the new ordering, so we must re-resolve rather than skip.
    const resolvedForIntentRef = useRef<string | null>(null);
    // Latest intent, set synchronously on every render so an in-flight resolve
    // whose intent went stale mid-flight (navigation / filter change) is
    // discarded instead of yanking the feed to a page computed for the old
    // selection. Excludes transient state (feedData / currentPage / loading) so a
    // benign feed refresh doesn't drop a valid jump.
    const resolveIntentRef = useRef<string>('');
    useEffect(() => {
        const targetId = expandedDocId;
        const intentKey = [
            targetId ?? '',
            selectedCategory,
            selectedSubcategory ?? '',
            searchQuery.trim(),
            showDeleted ? '1' : '0',
            sortBy,
            viewMode,
        ].join('|');
        resolveIntentRef.current = intentKey;

        if (!targetId || loading) return;
        // Row already on the current page → DocumentFeed scrolls + unfolds it.
        if (feedData.some((d) => d.doc_id === targetId)) {
            resolvedForIntentRef.current = intentKey;
            return;
        }
        if (resolvedForIntentRef.current === intentKey) return; // already handled this intent
        // taxonomy-v2 default (#1373): lens IDs are the v2 domain (fall back to category).
        const targetCategory =
            expandedMetadata?.classification?.domain ?? expandedMetadata?.classification?.category;
        if (!targetCategory) return; // metadata not loaded yet — wait
        // Only resolve once we're filtered to the doc's category; the
        // category-jump effect navigates there first, then this re-runs after
        // the page-0 fetch settles and the row is confirmed off-page.
        if (targetCategory !== selectedCategory) return;
        // #2009 slice 2: likewise wait for the category-jump effect to clear a
        // stale/mismatched ``?sub=`` before resolving. Otherwise this fires a
        // resolve (and page-jump) keyed on a legacy subcategory the target doc's
        // v2 role doesn't match, which the exact ``role=`` filter resolves to
        // found=false — the very hole the sub-clear above closes.
        const targetRole = expandedMetadata?.classification?.role ?? null;
        if (selectedSubcategory && selectedSubcategory !== targetRole) return;
        resolvedForIntentRef.current = intentKey;
        void (async () => {
            try {
                const result = await documentService.resolveDocumentPage(targetId, {
                    page_size: pageSize,
                    sort_by: sortBy,
                    view: viewMode,
                    // classifier-v3 (#2009): the lens IDs ARE the v2 (domain, role)
                    // cell — send them as the native axes so the locate matches the
                    // same signal the feed filters on (legacy category is frozen NULL).
                    ...(selectedCategory !== 'all' && { domain: selectedCategory }),
                    ...(selectedSubcategory && { role: selectedSubcategory }),
                    ...(searchQuery.trim() && { search: searchQuery.trim() }),
                    ...(showDeleted && { deleted_only: true }),
                });
                // Discard if the active deep-link / filter intent changed while
                // the request was in flight — otherwise we'd page the (now
                // different) feed to an offset computed for the old selection.
                if (resolveIntentRef.current !== intentKey) return;
                if (result?.found && result.page != null && result.page !== currentPage) {
                    setPage(result.page);
                }
            } catch {
                // Bounded fallback: leave the user on the category's first page.
            }
        })();
    }, [
        expandedDocId,
        loading,
        feedData,
        expandedMetadata,
        selectedCategory,
        selectedSubcategory,
        searchQuery,
        showDeleted,
        pageSize,
        sortBy,
        viewMode,
        currentPage,
        setPage,
    ]);

    // classifier-v3 (#1066): the (domain → role) taxonomy tree backs the lens nav
    // and the two-axis override picker. The legacy type-picker
    // (useTaxonomyTree/useOverrideDocumentType) was retired in #1065.
    const { data: taxonomyDomains } = useTaxonomyDomains(true);
    const overrideClassificationMutation = useOverrideClassification();
    const factEditMutation = useUpdateFactOverrides();
    const correctFactMutation = useCorrectFact();
    const [savingFieldId, setSavingFieldId] = useState<string | null>(null);

    const handleFactEdit = useCallback(
        (fieldId: string, value: string, pickedEntityId?: string) => {
            if (!previewState.currentDocId) return;
            setSavingFieldId(fieldId);

            // Issue #706: when the worker's L0 validator flagged this
            // field, route the edit through the *correction* endpoint
            // (POST /facts/correct) rather than the generic override
            // (PATCH /facts). The two diverge only on (a) the audit
            // event_type emitted (``document.fact_corrected`` vs
            // ``document.fact_overridden``) and (b) the recalc message
            // ``source`` label that drives ``classify_updates`` in the
            // worker — both load-bearing for the canary's acceptance
            // surface.
            const validation = metadata?._validation?.[fieldId];

            const onSuccess = () => {
                setSavingFieldId(null);
                showToast(t('documents.factEditSuccess') || 'Wert gespeichert', 'success');
                // #1575a: the fact/correction mutations only invalidate the
                // preview's ['document-metadata', docId] cache, not the feed
                // (useDocuments). Without this the list row stays stale until a
                // full reload. Mirror handleClassificationOverride, which already refreshes.
                refreshDocuments();
            };
            const onError = () => {
                setSavingFieldId(null);
                showToast(t('documents.factEditError') || 'Fehler beim Speichern des Werts', 'error');
            };

            if (validation) {
                correctFactMutation.mutate(
                    {
                        docId: previewState.currentDocId,
                        fieldId,
                        value,
                        validatorId: validation.validator_id,
                    },
                    { onSuccess, onError },
                );
            } else {
                factEditMutation.mutate(
                    { docId: previewState.currentDocId, fieldId, value, pickedEntityId },
                    { onSuccess, onError },
                );
            }
        },
        [previewState.currentDocId, factEditMutation, correctFactMutation, metadata, showToast, t, refreshDocuments],
    );

    // #386 subtask 05 — fact-field id → canonical entity_type. Drives the
    // autocomplete dropdown for entity-mapped fields in the document
    // preview. The set is intentionally conservative for the first cut;
    // future work can derive it from the ontology config served by the
    // backend rather than hard-coding here.
    const entityFieldMap = useMemo<Record<string, string>>(
        () => ({
            supplier_name: 'supplier',
            seller_name: 'supplier',
            vendor_name: 'supplier',
            buyer_name: 'organization',
            customer_name: 'organization',
            recipient_name: 'organization',
            sender_name: 'organization',
            organization_name: 'organization',
        }),
        [],
    );

    // Delegates to the extracted ``buildPreviewFacts`` builder (#1527 codex P3) so the
    // page→preview mapping — including the ``line_items`` → non-editable Buchungen table
    // wiring — is a single directly-tested function, not an inline memo.
    const previewFacts = useMemo<PreviewFact[] | null>(() => buildPreviewFacts(metadata), [metadata]);
    // FR-017 #5: the rows the "Verknüpfte Objekte" line picker offers — read from the same
    // metadata the preview already fetched, under the field the extraction recorded.
    const linkLines = useMemo(() => documentRows(metadata), [metadata]);

    // classifier-v3 (#1066): two-axis (domain, role) override — the sole
    // classification-override path since the legacy type override was retired
    // (#1065). Posts to the v3 endpoint; the backend reprocesses on the native
    // cell (the legacy-triple projection was removed with #2009).
    const handleClassificationOverride = useCallback(
        (cell: { domain: string; role: string }) => {
            if (!previewState.currentDocId) return;
            overrideClassificationMutation.mutate(
                { docId: previewState.currentDocId, payload: cell },
                {
                    onSuccess: () => {
                        handlePreviewClose();
                        showToast(t('documents.typeOverrideSuccess') || 'Dokumenttyp wird neu verarbeitet…', 'success');
                        refreshDocuments();
                    },
                    onError: () => {
                        showToast(t('documents.typeOverrideError') || 'Fehler beim Überschreiben des Dokumenttyps', 'error');
                    },
                },
            );
        },
        [previewState.currentDocId, overrideClassificationMutation, handlePreviewClose, showToast, t, refreshDocuments],
    );

    // Sync expanded document card with URL (?doc=<id>)
    const handleExpandedChange = useCallback((docId: string | null) => {
        setExpandedDocId(docId);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (docId) {
                next.set('doc', docId);
            } else {
                next.delete('doc');
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // Tag operations
    const {
        allTags,
        handleToggleTag,
        handleCreateTag,
        handleEditTag,
        handleDeleteTag,
    } = useTagActions({ feedData, refreshDocuments, updateDocumentTagsOptimistic });

    // Document CRUD and batch operations
    const {
        handleDelete,
        handleRestore,
        handleUpload,
        handleDownloadSelected,
        handleRestoreSelected,
        isDeleteConfirmOpen,
        handleDeleteSelected,
        handleConfirmDelete,
        handleCancelDelete,
        isReprocessConfirmOpen,
        handleReprocessSelected,
        handleConfirmReprocess,
        handleCancelReprocess,
    } = useDocumentActions({
        uploadBatch,
        refreshDocuments,
        showToast,
        showProgressToast,
        t,
        selectedDocIds,
        setSelectionMode,
        setSelectedDocIds,
    });

    // Convert backend sort values to design-system SortOption
    const sortOption: SortOption = sortBy === 'document_date' ? 'created' : 'uploaded';

    // Handle sort change from design system
    const handleSortChange = useCallback((sort: SortOption) => {
        const backendSort = sort === 'created' ? 'document_date' : 'received_date';
        setSortBy(backendSort as 'document_date' | 'received_date');
    }, [setSortBy]);

    // Handle view mode change from design system (also marks as viewed when switching to 'new')
    const handleViewModeChange = useCallback((mode: ViewMode) => {
        if (mode === 'new') {
            markAsViewed();
        } else {
            setViewMode(mode);
        }
    }, [markAsViewed, setViewMode]);

    // FR-017 #7a — "Ohne Verknüpfung": processed documents with no twin link.
    // Linking one from its panel refreshes the census; in the unlinked view the
    // feed follows it, so the linked document leaves the list with the count.
    const unlinkedCount = useUnlinkedCount();
    const lastUnlinkedCount = useRef(unlinkedCount);
    useEffect(() => {
        if (lastUnlinkedCount.current === unlinkedCount) return;
        lastUnlinkedCount.current = unlinkedCount;
        if (viewMode === 'unlinked') refreshDocuments();
    }, [unlinkedCount, viewMode, refreshDocuments]);

    // Build categories from backend counts (across all documents, not just current page)
    // #1889 — use totalAllVisible (non-``filtered`` count) for "Alle Dokumente"
    // so the sidebar badge equals the feed (which hides ``filtered`` docs);
    // totalAll counted logos/unparseable finance sheets → "Alle Dokumente 2"
    // over an empty feed.
    // classifier-v3 (#1066): the lens nav is driven by the native (domain, role)
    // counts (domains as lenses — IDs already aligned — roles as subcategories).
    // buildCategories consumes the {count, name, subcategories} structure. The
    // legacy categoryCounts source was removed with the classifier-v1
    // decommission (#2009).
    const lensCounts = domainCounts;
    const categories = useMemo(() => buildCategories(lensCounts, totalAllVisible), [lensCounts, totalAllVisible]);

    // Handle category selection
    // When filtering from 'new' view mode, switch back to 'all' (document date) view
    const handleCategorySelect = useCallback((categoryId: string, subcategoryId: string | null) => {
        setSelectedCategory(categoryId);
        setSelectedSubcategory(subcategoryId);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (categoryId && categoryId !== ALL_DOCUMENTS_LENS_ID) {
                next.set('category', categoryId);
            } else {
                next.delete('category');
            }
            if (subcategoryId) {
                next.set('sub', subcategoryId);
            } else {
                next.delete('sub');
            }
            // #2108 — the ``lens=all`` axis existed only to defeat the
            // retired starting-page gate. Nothing sets it anymore; scrub
            // it here so legacy bookmarks lose the dead param on their
            // first in-page navigation.
            next.delete('lens');
            return next;
        }, { replace: true });
        // FR-017 #7a review: the unlinked view's name carries the TENANT-wide
        // count, so a lens inside it would show a list the name disagrees with.
        if (viewMode !== 'all') {
            setViewMode('all');
        }
    }, [viewMode, setViewMode, setSearchParams]);

    // HOF-106 — the EXPLICIT selection path, and the only thing that writes the
    // preference. ``handleCategorySelect`` itself is also called by resolution
    // (#2009's jump to a deep-linked document's own domain), and merely landing
    // where a link put you is not a choice: recording it would let a shared link
    // silently redefine the reader's default. „Alle Dokumente" is stored as
    // itself — a deliberate return to the unfiltered view is a selection, and
    // without it the reader is thrown back into their old lens next visit.
    const handleCategorySelectExplicit = useCallback((categoryId: string, subcategoryId: string | null) => {
        writeStoredCategory(tenantId, categoryId || ALL_DOCUMENTS_LENS_ID);
        // And it spends the restore's one shot, whenever it happens — the same
        // rule as "only an explicit selection writes", read from the other end.
        // The readiness gate leaves a narrow window in which the shot is still
        // loaded: choose „Alle Dokumente" while the first feed request is in
        // flight and no `?category=` marker is left behind, so when the counts
        // land the restore fires and reverses a choice the reader has already
        // made — with `storedCategoryRaw`, which a write does not refresh, so it
        // restores the value read at mount. Codex round 2, P1.
        preferenceRestored.current = true;
        handleCategorySelect(categoryId, subcategoryId);
    }, [tenantId, handleCategorySelect]);

    // HOF-106 — restore the remembered lens on a bare /documents.
    //
    // Precedence: an explicit ``?category=`` (a statement about THIS visit)
    // beats the stored preference, which beats „Alle Dokumente". The stored id
    // is validated against the lenses the nav actually offers, because
    // ``buildCategories`` emits only domains with ``count > 0`` and this page
    // has no fallback for an unknown ``?category=``: it forwards it as a
    // ``domain=`` filter and renders an empty feed under a breadcrumb that
    // cannot name it. The URL parameter is deliberately NOT put through that
    // rule — narrowing deep links is a different change than remembering a
    // selection.
    //
    // Two gates, and both are load-bearing. ``!tenantResolved``: the preference
    // cannot be read until ``/auth/me`` lands, and spending the one shot before
    // then would make it unreachable for the whole visit. ``lensCounts`` empty:
    // the lens list is count-derived and arrives with the first feed response,
    // so judging a stored domain against the pre-load list — which holds only
    // the synthetic all-lens — would reject every one of them. Note this is not
    // the same as waiting for ``loading``: that flag is false on the very first
    // render, before the request has even been issued, so gating on it consumes
    // the shot against an empty list. A tenant with no documents at all never
    // consumes the shot, which costs nothing: it has no domain to restore.
    useEffect(() => {
        if (preferenceRestored.current) return;
        if (!tenantResolved || Object.keys(lensCounts).length === 0) return;
        // Spent at the first moment the page is ABLE to decide, not at the first
        // moment it decides to act — and so BEFORE the explicit-parameter check,
        // deliberately. Deferring it past a `?category=` deep link leaves the
        // shot loaded: clearing that parameter later (choosing „Alle Dokumente",
        // the back button) would then fire a first restore that overrides the
        // reader's own explicit selection with a preference read at mount, which
        // `storedCategoryRaw` does not even re-read after a write. An explicit
        // parameter present at readiness IS the decision — the URL won.
        // Codex round 1, P1.
        preferenceRestored.current = true;
        if (searchParams.get('category')) return;
        const target = resolveStoredCategory(storedCategoryRaw, categories);
        // The all-documents sentinel IS the absence of the parameter: restoring
        // it stamps nothing, rather than resurrecting the ``?category=all`` axis
        // #2108 retired and ``handleCategorySelect`` scrubs.
        if (!target || target === ALL_DOCUMENTS_LENS_ID) return;
        setSelectedCategory(target);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('category', target);
            return next;
        }, { replace: true });
    }, [tenantResolved, lensCounts, searchParams, storedCategoryRaw, categories, setSearchParams]);

    // Build breadcrumb items based on current selection
    const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
        const items: BreadcrumbItem[] = [];
        const rootLabel = showDeleted ? 'Gelöschte Dokumente' : 'Dokumente';

        if (selectedCategory === ALL_DOCUMENTS_LENS_ID) {
            items.push({ label: rootLabel });
            items.push({ label: 'Alle Dokumente' });
            return items;
        }

        items.push({
            label: rootLabel,
            onClick: () => handleCategorySelectExplicit(ALL_DOCUMENTS_LENS_ID, null),
        });

        const categoryItem = categories.find(c => c.id === selectedCategory);
        if (categoryItem) {
            if (selectedSubcategory) {
                items.push({
                    label: categoryItem.name,
                    onClick: () => handleCategorySelectExplicit(selectedCategory, null),
                });
            } else {
                items.push({ label: categoryItem.name });
            }
        }

        if (selectedSubcategory) {
            // Get subcategory name from categories structure (comes from backend API - #287)
            const category = categories.find(c => c.id === selectedCategory);
            const subcategory = category?.subcategories?.find(s => s.id === selectedSubcategory);
            const subcategoryName = subcategory?.name || selectedSubcategory;
            items.push({ label: subcategoryName });
        }

        return items;
    }, [selectedCategory, selectedSubcategory, showDeleted, handleCategorySelectExplicit, categories]);

    // Scroll to top when category/subcategory changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [selectedCategory, selectedSubcategory]);

    // Handle show deleted toggle
    // FR-017 #7a review: the trash and "Ohne Verknüpfung" are disjoint (an
    // unrouted document is by definition not deleted), so entering the trash
    // leaves the unlinked view rather than showing an empty trash.
    const handleToggleShowDeleted = useCallback(() => {
        setShowDeleted((prev) => !prev);
        if (viewMode === 'unlinked') setViewMode('all');
    }, [viewMode, setViewMode]);

    // Render content based on state
    const renderContent = () => {
        // Pre-data render — let the (delayed) PageLoadOverlay handle the
        // loading affordance; render the empty DocumentFeed scaffold so the
        // page layout commits immediately and there is no flat
        // ``<p class="loading">`` flash before the overlay appears (#762).
        if (error) {
            return (
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={refreshDocuments} className="retry-button">
                        {t('documents.retry')}
                    </button>
                </div>
            );
        }

        if (documentsWithDeletedFlag.length === 0) {
            const hasFilters = searchQuery.trim() || selectedCategory !== ALL_DOCUMENTS_LENS_ID || selectedSubcategory !== null;

            return (
                <DocumentFeed
                    documents={[]}
                    onPreview={handlePreview}
                    onDelete={handleDelete}
                    onRestore={handleRestore}
                    deleteLabel={t('documents.delete') || 'Delete'}
                    previewLabel={t('documents.preview') || 'Preview'}
                    restoreLabel={t('documents.restore') || 'Restore'}
                    noDocumentsFoundLabel={t('documents.noDocumentsFound')}
                    sortBy={sortBy}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalItems={hasFilters ? total : 0}
                    onPageChange={setPage}
                    selectionMode={selectionMode}
                    selectedDocIds={selectedDocIds}
                    onSelectionChange={handleSelectionChange}
                    showDeleted={showDeleted}
                    allTags={allTags}
                    onToggleTag={handleToggleTag}
                    onCreateTag={handleCreateTag}
                    onEditTag={handleEditTag}
                    onDeleteTag={handleDeleteTag}
                    tagLabel={t('tags.triggerLabel')}
                    tagPanelLabels={{
                        placeholder: t('tags.placeholder'),
                        done: t('tags.done'),
                        deleteTag: t('tags.deleteTag'),
                        cancel: t('tags.cancel'),
                    }}
                    expandedDocId={expandedDocId}
                    onExpandedChange={handleExpandedChange}
                    getPageCount={getPageCount}
                    pageThumbnailUrl={pageThumbnailUrl}
                    onPreviewPage={onPreviewPageTile}
                />
            );
        }

        return (
            <DocumentFeed
                documents={documentsWithDeletedFlag}
                onPreview={handlePreview}
                onDelete={handleDelete}
                onRestore={handleRestore}
                deleteLabel={t('documents.delete') || 'Delete'}
                previewLabel={t('documents.preview') || 'Preview'}
                restoreLabel={t('documents.restore') || 'Restore'}
                noDocumentsFoundLabel={t('documents.noDocumentsFound')}
                sortBy={sortBy}
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={total}
                onPageChange={setPage}
                selectionMode={selectionMode}
                selectedDocIds={selectedDocIds}
                onSelectionChange={handleSelectionChange}
                showDeleted={showDeleted}
                allTags={allTags}
                onToggleTag={handleToggleTag}
                onCreateTag={handleCreateTag}
                onEditTag={handleEditTag}
                onDeleteTag={handleDeleteTag}
                expandedDocId={expandedDocId}
                onExpandedChange={handleExpandedChange}
                getPageCount={getPageCount}
                pageThumbnailUrl={pageThumbnailUrl}
                onPreviewPage={onPreviewPageTile}
            />
        );
    };

    return (
        <>
            <PageLoadOverlay
                visible={pageLoadOverlay.showOverlay}
                elapsedSeconds={pageLoadOverlay.elapsedSeconds}
            />
            <DocumentsPageLayout
                categories={categories}
                selectedCategory={selectedCategory}
                selectedSubcategory={selectedSubcategory}
                onCategorySelect={handleCategorySelectExplicit}
                lensePanelTitle={t('documents.panelTitle', 'Digital Archive')}
                sortBy={sortOption}
                onSortChange={handleSortChange}
                onUploadClick={() => setIsUploadModalOpen(true)}
                title={t('documents.title')}
                uploadLabel={t('documents.upload') || 'Hochladen'}
                sortCreatedLabel={t('documents.sortCreated') || 'Created'}
                sortUploadedLabel={t('documents.sortUploaded') || 'Uploaded'}
                // New docs view mode
                newDocsCount={newDocsCount}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                newDocsLabel={t('documents.newDocs') || 'New documents'}
                newReceivedLabel={t('documents.newReceived') || '{{count}} new received'}
                unlinkedCount={unlinkedCount}
                unlinkedLabel={t('documents.unlinked', 'Ohne Verknüpfung ({{count}})')}
                backToAllLabel={t('documents.backToAll') || 'Back to all'}
                orderedByPrefix={t('documents.sortOrderedBy')}
                documentDateOptionLabel={t('documents.sortDocumentDateOption') || 'Document date (issued)'}
                receivedDateOptionLabel={t('documents.sortReceivedDateOption') || 'Received date (uploaded)'}
                // Selection mode
                selectionMode={selectionMode}
                onEnterSelectionMode={handleEnterSelectionMode}
                onExitSelectionMode={handleExitSelectionMode}
                selectedCount={selectedDocIds.size}
                totalCount={documentsWithDeletedFlag.filter((doc) => doc.status === 'completed' || doc.status === 'error').length}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onDownloadSelected={handleDownloadSelected}
                onDeleteSelected={handleDeleteSelected}
                onRestoreSelected={handleRestoreSelected}
                onReprocessSelected={handleReprocessSelected}
                reprocessSelectedLabel={t('documents.reprocessSelected') || 'Reprocess selected'}
                selectDocumentsLabel={t('documents.selectDocuments') || 'Select documents'}
                cancelSelectionLabel={t('documents.cancelSelection') || 'Cancel selection'}
                downloadSelectedLabel={t('documents.downloadSelected') || 'Download selected'}
                deleteSelectedLabel={t('documents.deleteSelected') || 'Delete selected'}
                restoreSelectedLabel={t('documents.restoreSelected') || 'Restore selected'}
                selectedLabel={t('documents.selected') || '{count} selected'}
                cancelLabel={t('common.cancel') || 'Cancel'}
                selectAllLabel={t('documents.selectAll') || 'Select all'}
                // Cross-page select-all
                showSelectAllBanner={showSelectAllBanner}
                onSelectAllMatching={handleSelectAllMatching}
                allMatchingSelected={allMatchingSelected}
                totalMatching={total}
                loadingAllIds={loadingAllIds}
                selectAllMatchingLabel={t('documents.selectAllMatching') || 'Select all {count} matching documents'}
                allSelectedConfirmLabel={t('documents.allSelectedConfirm') || 'All {count} documents selected.'}
                // Show deleted documents
                showDeleted={showDeleted}
                onShowDeletedToggle={handleToggleShowDeleted}
                showDeletedLabel={t('documents.showDeleted') || 'Show deleted documents'}
                hideDeletedLabel={t('documents.hideDeleted') || 'Hide deleted documents'}
                // Breadcrumb
                breadcrumbItems={breadcrumbItems}
            >
                {renderContent()}
            </DocumentsPageLayout>

            <UploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onUpload={handleUpload}
            />

            <ConfirmationModal
                isOpen={isDeleteConfirmOpen}
                title={t('documents.deleteConfirmTitle') || 'Delete Documents'}
                message={
                    t('documents.deleteConfirmMessage', { count: selectedDocIds.size }) ||
                    `Are you sure you want to delete ${selectedDocIds.size} document(s)? This action cannot be undone.`
                }
                confirmLabel={t('common.delete') || 'Delete'}
                cancelLabel={t('common.cancel') || 'Cancel'}
                danger
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />

            <ConfirmationModal
                isOpen={isReprocessConfirmOpen}
                title={t('documents.reprocessConfirmTitle') || 'Reprocess Documents'}
                message={
                    t('documents.reprocessConfirmMessage', { count: selectedDocIds.size }) ||
                    `Are you sure you want to reprocess ${selectedDocIds.size} document(s)? Classification and extraction will be redone.`
                }
                confirmLabel={t('documents.reprocess') || 'Reprocess'}
                cancelLabel={t('common.cancel') || 'Cancel'}
                onConfirm={handleConfirmReprocess}
                onCancel={handleCancelReprocess}
            />

            <DocumentPreview
                isOpen={previewState.isOpen}
                documentUrl={previewState.documentUrl}
                filename={previewState.filename}
                onClose={handlePreviewClose}
                onDownload={handlePreviewDownload}
                onRotate={handlePreviewRotate}
                rotating={rotating}
                loading={previewState.loading}
                error={previewState.error}
                backLabel={t('documents.backToDocuments') || 'Back to documents'}
                downloadLabel={t('documents.download') || 'Download'}
                printLabel={t('documents.print') || 'Print'}
                rotateLabel={t('documents.rotate') || 'Drehen'}
                closeLabel={t('common.close') || 'Close'}
                loadingLabel={t('documents.loadingDocument') || 'Loading document...'}
                noPreviewLabel={t('documents.noInlinePreview') || 'Vorschau für diesen Dateityp nicht verfügbar'}
                facts={previewFacts}
                factsLoading={metadataLoading}
                classification={metadata?.classification ?? null}
                classifierV3Enabled={true}
                taxonomyDomains={taxonomyDomains}
                onClassificationOverride={handleClassificationOverride}
                classificationOverrideLoading={overrideClassificationMutation.isPending}
                onFactEdit={handleFactEdit}
                factSaveStatus={
                    factEditMutation.isPending
                        ? 'saving'
                        : factEditMutation.isError
                            ? 'error'
                            : factEditMutation.isSuccess
                                ? 'success'
                                : 'idle'
                }
                savingFieldId={savingFieldId}
                entityFieldMap={entityFieldMap}
                fetchEntitySuggestions={fetchEntitySuggestions}
                documentList={previewDocumentList}
                currentIndex={previewState.currentIndex}
                initialPage={previewState.initialPage}
                onNavigate={handleNavigate}
                canNavigatePrev={canNavigatePrev}
                canNavigateNext={canNavigateNext}
                verifiedBadge={<DocumentVerifiedBadgeContainer documentId={previewState.currentDocId} />}
                twinLinksSlot={
                    <DocumentTwinLinksContainer
                        documentId={previewState.currentDocId}
                        lines={linkLines}
                        documentDate={metadata?.document_date ?? null}
                    />
                }
            />

            {/*
              Per-document audit history. Mounted only when the read-path
              flag is on AND the preview overlay has a current doc. Pre-cutover
              the underlying useAuditEvents hook returns []; final visual
              integration inside the preview modal lands in 06-frontend-cutover.
            */}
            {auditReadEnabled && previewState.isOpen && previewState.currentDocId && (
                <DocumentHistorySection docId={previewState.currentDocId} />
            )}
        </>
    );
};

export default DocumentsPage;
